import React, { useState, useEffect } from 'react';

// Inline styles for the landing page
const styles = `
  @keyframes fadeUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  .animate-fadeUp {
    animation: fadeUp 1s ease-out forwards;
  }
  @keyframes gradientMove {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
  .animate-gradient {
    background-size: 200% 200%;
    animation: gradientMove 8s ease infinite;
  }
  @keyframes slowZoom {
    from {
      transform: scale(1.05);
    }
    to {
      transform: scale(1.1);
    }
  }
  .animate-slowZoom {
    animation: slowZoom 20s ease-in-out infinite alternate;
  }
  .delay-300 {
    animation-delay: 0.3s !important;
  }
  .delay-500 {
    animation-delay: 0.5s !important;
  }
  .delay-700 {
    animation-delay: 0.7s !important;
  }
`;

function useInView(ref, rootMargin = "0px") {
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIntersecting(entry.isIntersecting),
      { rootMargin }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, rootMargin]);

  return isIntersecting;
}

function ContactSection({ lang }) {
  const ref = React.useRef(null);
  const isInView = useInView(ref, "-50px");

  return (
    <section
      id="contact"
      ref={ref}
      className={`relative py-20 transition-all duration-1000 ease-out ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"
        }`}
    >
      <div className="absolute inset-0 bg-black/60 transition-all duration-1000"></div>
      <div className="relative mx-auto max-w-xl px-6 text-center">
        <h2
          className={`text-3xl sm:text-4xl font-light mb-4 tracking-wide transition-all duration-1000 delay-200 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            } ${lang === "ar" ? "font-arabic" : "font-['Playfair_Display']"}`}
        >
          {lang === "ar" ? "تواصل معنا" : "Contact Us"}
        </h2>
        <p
          className={`text-white/60 text-base mb-8 transition-all duration-1000 delay-400 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
        >
          {lang === "ar"
            ? "نحن هنا للإجابة على استفساراتك."
            : "We're here to answer your inquiries."}
        </p>
        <div className="space-y-3 text-lg">
          <p
            className={`transition-all duration-1000 delay-600 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
          >
            <a
              href="mailto:Info@raffak.store"
              className="hover:underline hover:decoration-primary transition"
            >
              Info@raffak.store
            </a>
          </p>
          <p
            className={`transition-all duration-1000 delay-800 ${isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
          >
            <a
              href="https://wa.me/971557330893"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline hover:decoration-primary transition"
            >
              +971 55 733 0893
            </a>
          </p>
        </div>
      </div>
    </section>
  );
}

const RaffakLandingPage = () => {
  const [lang, setLang] = useState("en");
  const [scrolled, setScrolled] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [active, setActive] = useState("All");

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const translations = {
    en: {
      heroTitle: "Raffak Studio",
      heroDesc: "Minimal design, modern touch — bringing brand and digital together.",
      explore: "Explore Work",
      about: "About Us",
      filters: ["All", "Web", "App"],
      footer: "© " + new Date().getFullYear() + " Raffak — Crafted with care.",
    },
    ar: {
      heroTitle: "استوديو رفّك",
      heroDesc: "تصميم بسيط، لمسة عصرية — ندمج العلامة التجارية والرقمي معًا.",
      explore: "استعرض الأعمال",
      about: "معلومات عنا",
      filters: ["الكل", "ويب", "تطبيق"],
      footer: "© " + new Date().getFullYear() + " رفّك — صُنِع بعناية.",
    },
  };

  const categoryMap = {
    الكل: "All",
    ويب: "Web",
    تطبيق: "App",
  };

  const projects = [
    {
      title: "Sumaya — Creative Portfolio",
      blurb: "Elegant bilingual portfolio with smooth motion and refined gradients.",
      tags: ["Web design", "React", "Tailwind"],
      category: "Web",
      href: "https://portfolio-sumaya.web.app/",
      image: "https://res.cloudinary.com/dwdz5jjnf/image/upload/v1759676754/White_and_Brown_Modern_Interior_Design_Photo_Collage_tffpuq.png",
    },
    {
      title: "Korean Beauty Store",
      blurb: "E-commerce store for Korean beauty products — clean UI + shop flow.",
      tags: ["E-commerce", "React", "Storefront"],
      category: "Web",
      href: "https://koreanbeauty.raffak.store/",
      image: "https://res.cloudinary.com/dwdz5jjnf/image/upload/v1760121030/Black_Photo_Collage_Moodboard_Grid_Brand_Logo_Color_Palette_Instagram_Post_1_uqfbi3.png",
    },
    {
      title: "Lume Store",
      blurb: "Modern store layout for Lume — sleek visuals and optimized UX.",
      tags: ["Web", "E-commerce", "Storefront"],
      category: "Web",
      href: "https://lume.raffak.store/",
      image: "https://res.cloudinary.com/dwdz5jjnf/image/upload/v1760121034/Aesthetic_Grid_Photo_Collage_October_Wellness_Moodboard_Instagram_Post_w7opct.png",
    },
    {
      title: "Biz Recipy — Meal Subscription App",
      blurb: "Subscription-based meal delivery app with full customization.",
      tags: ["App", "React Native", "Firebase", "iOS / Android"],
      category: "App",
      href: "https://testflight.apple.com/join/MMyfSxea",
      image: "https://res.cloudinary.com/dwdz5jjnf/image/upload/v1760121922/Brown_Beige_Simple_Grid_Fashion_Spring_Collection_Facebook_Post_2_ui6753.png",
    },
    {
      title: "Tamimi Lawyer — Legal Services Website",
      blurb: "Professional legal firm website with bilingual content.",
      tags: ["Web design", "Corporate", "Next.js / Firebase"],
      category: "Web",
      href: "https://tamimi-lawyer.web.app/",
      image: "https://res.cloudinary.com/dwdz5jjnf/image/upload/v1760188659/Brown_Collage_Quote_Instagram_Post_edevvo.png",
    },
  ];

  const visible = projects.filter((p) => active === "All" || p.category === active);

  return (
    <>
      <style>{styles}</style>
      <div className={`min-h-screen bg-gradient-to-br from-[#8b5a7b] via-[#8b5a7b] to-[#8b5a7b] text-white overflow-x-hidden ${lang === "ar" ? "font-arabic" : "font-sans"}`}>
        {/* Header */}
        <header className={`fixed top-0 z-40 w-full transition-colors duration-500 ${scrolled ? "bg-black/90 backdrop-blur-md shadow-lg" : ""}`}>
          <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-1">
            <a href="/" className={`${lang === "ar" ? "font-arabic text-2xl" : "text-3xl"} tracking-wider transition-colors hover:text-[#d6a7c2]`}>
              {lang === "ar" ? "رفّك" : "Raffak"}
            </a>
            <div className="flex items-center gap-8">
              <nav className="hidden sm:flex items-center gap-6 text-sm uppercase tracking-widest">
                <a href="#work" className="hover:text-[#d6a7c2] transition-colors">{lang === "ar" ? "الأعمال" : "Work"}</a>
                <button onClick={() => setInfoOpen(true)} className="hover:text-[#d6a7c2] transition-colors">{lang === "ar" ? "معلومات" : "Info"}</button>
                <a href="#contact" className="hover:text-[#d6a7c2] transition-colors">{lang === "ar" ? "تواصل" : "Contact"}</a>
              </nav>
              <button onClick={() => setLang(lang === "en" ? "ar" : "en")} className="text-sm px-3 py-1 border border-white/40 rounded-full hover:bg-white/10 transition uppercase tracking-widest">
                {lang === "en" ? "ع" : "EN"}
              </button>
            </div>
          </div>
        </header>

        {/* Hero */}
        <section className="relative h-screen flex items-center justify-center text-center text-white overflow-hidden">
          <div className="relative z-10 px-4">
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-light tracking-wide leading-tight text-white opacity-0 animate-fadeUp">
              {lang === "ar" ? <><span className="text-[#8b5a7b] font-bold">1500</span> درهم فقط بـ</> : <>Only <span className="text-[#8b5a7b] font-bold">1500</span> AED</>}
            </h1>
            <p className="mt-6 text-2xl sm:text-3xl font-semi text-white/90 tracking-wide leading-relaxed opacity-0 animate-fadeUp delay-500">
              {lang === "ar" ? "متجرك الإلكتروني الفاخر — جاهز في يومين" : "Your luxury online store — ready in two days"}
            </p>
            <div className="mt-12 flex flex-wrap justify-center gap-6">
              <a href="#plans" className="rounded-none border border-white px-10 py-3 text-base font-medium text-white hover:bg-white hover:text-black transition-all duration-500 opacity-0 animate-fadeUp">
                {lang === "ar" ? "ابدأ الآن" : "Get Started"}
              </a>
              <a href="#contact" className="rounded-none px-10 py-3 text-base font-medium text-white/80 hover:text-white transition-all duration-500 opacity-0 animate-fadeUp delay-700">
                {lang === "ar" ? "تواصل معنا" : "Contact Us"}
              </a>
            </div>
          </div>
          <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover scale-105 animate-slowZoom">
            <source src="https://res.cloudinary.com/dwdz5jjnf/video/upload/v1760119369/Purple_Modern_Animated_Artificial_Intelligence_Video_1_pgqrkc.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-b from-transparent to-[#8b5a7b]"></div>
        </section>

        {/* Projects Section - Simplified */}
        <main id="work" className="mx-auto max-w-7xl px-4 py-24">
          <div className="text-center mb-16">
            <h2 className="text-5xl tracking-wide text-white mb-12">
              {lang === "ar" ? "استكشف أعمالنا" : "Explore our projects"}
            </h2>
            <div className="flex flex-wrap gap-3 justify-center">
              {translations[lang].filters.map((f) => {
                const normalized = lang === "ar" ? categoryMap[f] : f;
                return (
                  <button
                    key={normalized}
                    onClick={() => setActive(normalized)}
                    className={`rounded-full px-6 py-2 text-sm tracking-widest uppercase transition-all duration-500 ${active === normalized ? "bg-white text-black shadow-lg scale-105" : "bg-white/10 text-white hover:bg-white/20"
                      }`}
                  >
                    {f}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Projects Grid */}
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((p, i) => (
              <article key={p.title + i} className="group relative overflow-hidden rounded border border-white/20 bg-white/5 backdrop-blur-sm shadow-lg transition-all duration-700 hover:shadow-2xl hover:scale-[1.02]">
                <img src={p.image} alt={p.title} className="h-80 w-full object-cover transition-transform duration-1000 group-hover:scale-110" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-70 group-hover:opacity-90 transition duration-700"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 text-left">
                  <h3 className="text-2xl font-light text-white tracking-wide">{p.title}</h3>
                  <span className="block mt-1 h-[1px] w-0 bg-[#d6a7c2] transition-all duration-700 group-hover:w-20"></span>
                  <p className="mt-3 text-sm text-white/70 opacity-0 group-hover:opacity-100 transition-all duration-700">{p.blurb}</p>
                  <div className="mt-4 flex flex-wrap gap-2 opacity-0 group-hover:opacity-100 transition duration-700">
                    {p.tags.map((t) => (
                      <span key={t} className="rounded-full bg-white/10 px-3 py-1 text-xs tracking-wide">{t}</span>
                    ))}
                  </div>
                </div>
                <a href={p.href} target="_blank" rel="noopener noreferrer" className="absolute inset-0" aria-label={"Open " + p.title} />
              </article>
            ))}
          </div>
        </main>

        {/* Why Choose Us Section */}
        <section id="why" className="relative py-28 bg-gradient-to-b from-[#d6a7c2] via-[#d6a7c2]/80 to-[#8b5a7b]/90">
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5"></div>

          <div className="relative mx-auto max-w-6xl px-6 text-center">
            {/* Tagline */}
            <span className="uppercase tracking-[0.3em] text-sm text-white/60 block mb-4 animate-fadeUp">
              {lang === "ar" ? "لماذا تختارنا" : "Why Choose Us"}
            </span>

            {/* Luxury headline */}
            <h2 className={`${lang === "ar" ? "font-arabic" : "font-display"} text-4xl sm:text-5xl font-light text-white mb-16 animate-fadeUp delay-300`}>
              {lang === "ar" ? "التفرد والجودة في كل تفصيل" : "Exclusivity & Quality in Every Detail"}
            </h2>

            {/* Feature grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12 mb-16">
              {[
                {
                  title: lang === "ar" ? "دعم مستمر" : "Continuous Support",
                  desc: lang === "ar" ? "مساعدة فورية وقت ما تحتاج." : "Instant help whenever you need it.",
                  icon: "🤝",
                  color: "text-pink-300",
                },
                {
                  title: lang === "ar" ? "لوحة تحكم" : "Admin Dashboard",
                  desc: lang === "ar" ? "إدارة سهلة وتحكم كامل — لوحة تحكم مرنة لإدارة المنتجات، الطلبات، والعملاء بكل سهولة." : "Easy management and full control — a flexible dashboard to manage products, orders, and customers effortlessly.",
                  icon: "⚙️",
                  color: "text-sky-300",
                },
                {
                  title: lang === "ar" ? "دفع لمرة واحدة" : "One-Time Payment",
                  desc: lang === "ar" ? "ادفع مرة واحدة فقط، يشمل حتى 10 منتجًا." : "Pay once",
                  icon: "💳",
                  color: "text-pink-300",
                },
              ].map((item, i) => (
                <div
                  key={item.title}
                  className="relative p-8 border border-white/10 rounded-xl backdrop-blur-sm bg-white/5 hover:bg-white/10 transition-all duration-700 opacity-0 translate-y-8 animate-fadeUp"
                  style={{ animationDelay: `${0.3 + i * 0.2}s` }}
                >
                  <div className={`text-4xl mb-6 ${item.color}`}>{item.icon}</div>
                  <h3 className={`text-2xl text-white mb-4 ${lang === "ar" ? "font-arabic" : "font-['Playfair_Display']"}`}>
                    {item.title}
                  </h3>
                  <p className={`text-white/70 text-sm leading-relaxed ${lang === "ar" ? "font-arabic" : "font-sans"}`}>
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Free Trial Banner */}
          <section className="relative py-2 pt-[10px] text-center overflow-hidden bg-gradient-to-tr from-[#8b5a7b] via-[#d6a7c2] to-[#8b5a7b] animate-gradient">
            <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-white/10 blur-3xl"></div>

            <div className="relative mx-auto max-w-2xl px-6">
              <span className="inline-block mb-6 px-4 py-1.5 rounded-full text-xs tracking-widest uppercase bg-white/10 text-white/80 backdrop-blur-sm">
                {lang === "ar" ? "لفترة محدودة" : "Limited Time Offer"}
              </span>

              <h3 className={`text-4xl sm:text-5xl font-light mb-6 leading-tight animate-fadeUp ${lang === "ar" ? "font-arabic" : "font-display"}`}>
                {lang === "ar" ? "جرّب 10 أيام مجاناً — بلا مخاطر" : "Start Your 10-Day Free Trial"}
              </h3>

              <p className={`text-white/70 text-lg sm:text-xl mb-10 animate-fadeUp delay-300 ${lang === "ar" ? "font-arabic" : "font-sans"}`}>
                {lang === "ar" ? "شاهد النتائج خلال أول أسبوع — أو استرجع أموالك بالكامل." : "See results in your first week — or get a full refund."}
              </p>

              <a href="#plans" className="inline-block px-12 py-4 rounded-full text-lg font-medium bg-white text-[#8b5a7b] shadow-xl hover:bg-[#d6a7c2] hover:text-black transition-all duration-500 animate-fadeUp delay-500 mb-16">
                {lang === "ar" ? "ابدأ مجاناً الآن" : "Try It Free"}
              </a>
            </div>
          </section>
        </section>

        {/* Plans Section */}
        <section id="plans" className="relative py-28 bg-gradient-to-b from-[#8b5a7b] via-[#8b5a7b]/95 to-[#d6a7c2] text-white">
          <div className="relative mx-auto max-w-6xl px-6 text-center">
            <h2 className={`text-4xl sm:text-5xl font-light mb-16 animate-fadeUp delay-200 ${lang === "ar" ? "font-arabic" : "font-['Playfair_Display']"}`}>
              {lang === "ar" ? "خطط تناسب جميع الاحتياجات" : "Plans for Every Need"}
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-12">
              {/* Basic Plan */}
              <div className="relative flex flex-col p-10 rounded-2xl backdrop-blur-lg border bg-white/10 border-white/20 hover:scale-105 transition-all duration-700 animate-fadeUp">
                <h3 className="text-2xl mb-4">{lang === "ar" ? "خطة المبتدئين" : "Starter Plan"}</h3>
                <p className="text-3xl font-bold text-[#d6a7c2] mb-2">{lang === "ar" ? "ابتداءً من 1500 درهم" : "From AED 1500"}</p>
                <p className="text-xs text-[#d6a7c2]/80 mb-4">{lang === "ar" ? "أو ادفع بالأقساط - دفعتين بدون فوائد" : "Or pay in installments - 2 payments, no interest"}</p>
                <ul className="space-y-3 text-white/80 text-sm mb-8 text-left">
                  <li>✔ {lang === "ar" ? "قوالب جاهزة" : "Pre-built templates"}</li>
                  <li>✔ {lang === "ar" ? "حتى 10 منتجات" : "Up to 10 products"}</li>
                  <li>✔ {lang === "ar" ? "دفع آمن" : "Secure payments"}</li>
                  <li>✔ {lang === "ar" ? "متعدد اللغات" : "Multi-language"}</li>
                  <li>✔ {lang === "ar" ? "لوحة تحكم كاملة" : "Full admin dashboard"}</li>
                  <li>✔ {lang === "ar" ? "إدارة أكواد الخصم" : "Promo codes management"}</li>
                  <li>✔ {lang === "ar" ? "نظام الإعلانات" : "Announcements system"}</li>
                  <li>✔ {lang === "ar" ? "دعم كامل" : "Full support"}</li>
                  <li>✔ {lang === "ar" ? "صفحات أساسية: من نحن، المتجر، تواصل معنا" : "Essential pages: About us, Shop, Contact us"}</li>
                  <li>✔ {lang === "ar" ? "نطاق فرعي مجاني" : "Free subdomain"}</li>
                  <li>✔ {lang === "ar" ? "شهادة SSL مشمولة تلقائياً" : "SSL included automatically"}</li>
                  <li>✔ {lang === "ar" ? "بدون تعقيدات إعداد النطاق" : "No domain setup hassles"}</li>
                </ul>
                <div className="mt-auto">
                  <a href="#contact" className="block w-full py-3 rounded-full border border-white/30 hover:bg-white hover:text-black transition text-center">
                    {lang === "ar" ? "تواصل معنا" : "Contact Us"}
                  </a>
                </div>
              </div>

              {/* Premium Plan */}
              <div className="relative flex flex-col p-10 rounded-2xl backdrop-blur-lg border bg-white/10 border-white/20 hover:scale-105 transition-all duration-700 animate-fadeUp delay-200">
                <span className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#d6a7c2] text-black text-xs px-4 py-1 rounded-full shadow-md">
                  {lang === "ar" ? "الأكثر شيوعاً" : "Most Popular"}
                </span>
                <h3 className="text-2xl mb-4">{lang === "ar" ? "خطة النمو" : "Growth Plan"}</h3>
                <p className="text-3xl font-bold text-[#d6a7c2] mb-2">{lang === "ar" ? "ابتداءً من +1500 درهم" : "From AED 1500+"}</p>
                <p className="text-sm text-[#d6a7c2]/70 mb-4">{lang === "ar" ? "مع اشتراك شهري بسيط لتغطية التخزين (منتجات غير محدودة)" : "Includes a small monthly subscription for storage (unlimited products)"}</p>
                <p className="text-xs text-[#d6a7c2]/80 mb-6">{lang === "ar" ? "أو ادفع بالأقساط - دفعتين بدون فوائد" : "Or pay in installments - 2 payments, no interest"}</p>
                <ul className="space-y-3 text-white/80 text-sm mb-8 text-left">
                  <li>✔ {lang === "ar" ? "تشمل كل ما في الخطة الأساسية، بالإضافة إلى:" : "Includes everything in Basic Plan, plus:"}</li>
                  <li>✔ {lang === "ar" ? "نقدّم خدمة نقل البيانات من شركاتكم السابقة" : "Optional Migration from old providers to our platform"}</li>
                  <li>✔ {lang === "ar" ? "إدخال البيانات عند الطلب" : "Optional data entry service"}</li>
                  <li>✔ {lang === "ar" ? "تصميم الصور" : "Images designing"}</li>
                  <li>✔ {lang === "ar" ? "صفحة الشحن والتوصيل" : "Shipping & delivery page"}</li>
                  <li>✔ {lang === "ar" ? "صفحة سياسة الإرجاع والاستبدال" : "Return & exchange policy page"}</li>
                  <li>✔ {lang === "ar" ? "صفحة الأسئلة الشائعة" : "FAQ page"}</li>
                  <li>✔ {lang === "ar" ? "التكلفة الشهرية تشمل: التخزين السحابي، تسليم البريد الإلكتروني، النسخ الاحتياطي، تحديثات الأمان" : "Monthly fee covers: cloud storage, email delivery, backups, security updates"}</li>
                  <li>✔ {lang === "ar" ? "اتصال نطاق مخصص" : "Custom domain connection"}</li>
                </ul>
                <div className="mt-auto">
                  <a href="#contact" className="block w-full py-3 rounded-full bg-[#d6a7c2] text-[#8b5a7b] font-semibold hover:bg-white hover:text-black transition text-center shadow-lg">
                    {lang === "ar" ? "تواصل معنا" : "Contact Us"}
                  </a>
                </div>
              </div>

              {/* Custom Plan */}
              <div className="relative flex flex-col p-10 rounded-2xl backdrop-blur-lg border bg-white/10 border-white/20 hover:scale-105 transition-all duration-700 animate-fadeUp delay-400">
                <h3 className="text-2xl mb-4">{lang === "ar" ? "تخصيص كامل" : "Custom Web/ Mobile App"}</h3>
                <p className="text-3xl font-bold text-[#d6a7c2] mb-6">{lang === "ar" ? "السعر عند الطلب" : "Price on Request"}</p>
                <ul className="space-y-3 text-white/80 text-sm mb-8 text-left">
                  <li>✔ {lang === "ar" ? "تطوير مخصص من الصفر" : "Built from scratch"}</li>
                  <li>✔ {lang === "ar" ? "تصميم وهوية كاملة" : "Full custom design"}</li>
                  <li>✔ {lang === "ar" ? "منتجات غير محدودة" : "Unlimited products"}</li>
                  <li>✔ {lang === "ar" ? "دفع آمن" : "Secure payments"}</li>
                  <li>✔ {lang === "ar" ? "متعدد اللغات" : "Multi-language"}</li>
                  <li>✔ {lang === "ar" ? "دعم 24/7" : "24/7 support"}</li>
                </ul>
                <div className="mt-auto">
                  <a href="#contact" className="block w-full py-3 rounded-full border border-white/30 hover:bg-white hover:text-black transition text-center">
                    {lang === "ar" ? "تواصل معنا" : "Contact Us"}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <ContactSection lang={lang} />

        {/* Footer */}
        <footer className="bg-[#8b5a7b] border-t border-white/10 py-8 text-center text-sm text-white/80">
          {translations[lang].footer}
        </footer>

        {/* Info Panel */}
        {infoOpen && (
          <div className="fixed inset-0 z-50 flex">
            <div className="flex-1 bg-black/40 backdrop-blur transition-opacity duration-500" onClick={() => setInfoOpen(false)} />
            <aside className="fixed top-0 right-0 h-full w-full max-w-md overflow-y-auto bg-[#8b5a7b] border-l border-white/10 px-10 py-14 transform transition-transform duration-700 ease-out shadow-2xl" dir={lang === "ar" ? "rtl" : "ltr"}>
              <button onClick={() => setInfoOpen(false)} className="absolute right-6 top-6 rounded-full p-2 bg-white/10 text-white/80 hover:text-white hover:bg-white/20 transition">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <h2 className="text-4xl font-light tracking-wide text-white mb-6">{lang === "ar" ? "استوديو رفّك" : "Raffak — Studio"}</h2>
              <p className="text-white/90 text-base sm:text-lg leading-relaxed mb-10">
                {lang === "ar"
                  ? "نحن نؤمن أن نجاح المتجر يبدأ من التفاصيل. منصتنا تمزج بين التصميم الراقي والإدارة السهلة."
                  : "At Raffak Studio, we merge contemporary elegance with refined simplicity to craft exceptional digital experiences."}
              </p>
              <div className="h-[1px] w-full bg-white/20 mb-10"></div>
              <div className="space-y-6 text-white/90 text-sm sm:text-base">
                <p>
                  <span className="uppercase tracking-widest text-xs text-white/60 block mb-1">{lang === "ar" ? "الخدمات" : "Services"}</span>
                  {lang === "ar" ? "تصميم المنتجات والتطبيقات · تطوير المواقع" : "Product & App Design · Web Development"}
                </p>
                <p>
                  <span className="uppercase tracking-widest text-xs text-white/60 block mb-1">{lang === "ar" ? "تواصل معنا" : "Contact"}</span>
                  <a className="underline decoration-white/60 underline-offset-4 hover:text-white" href="mailto:info@raffak.store">info@raffak.store</a>
                </p>
              </div>
            </aside>
          </div>
        )}
      </div>
    </>
  );
};

export default RaffakLandingPage;
